import * as React from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SpeedIcon from '@mui/icons-material/Speed';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import BoltIcon from '@mui/icons-material/Bolt';
import ScienceIcon from '@mui/icons-material/Science';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import AirIcon from '@mui/icons-material/Air';
import CompressIcon from '@mui/icons-material/Compress';
import LightModeIcon from '@mui/icons-material/LightMode';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import GaugeMeter from './GaugeMeter';
import {
  ThermometerMeter,
  LevelMeter,
  HumidityMeter,
  CO2Meter,
  LightMeter,
  TimeSeriesPlot,
} from './meters';
import { useSensorView } from '../context/SensorViewContext';
import { SENSOR_TYPES, MOCK_SENSOR_VALUES, MOCK_RGB_DATA, MOCK_TRENDS, getStatusColorGradient, getSensorConfigWithPreset } from '../context/SensorConfigContext';
import { useGrow } from '../context/GrowContext';
import SensorSettingsOverlay from './SensorSettingsOverlay';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';

// Import live sensor context
import { useLiveSensors } from '../context/LiveSensorContext';

// Icon mapping
const ICON_MAP = {
  WaterDrop: WaterDropIcon,
  Bolt: BoltIcon,
  Science: ScienceIcon,
  DeviceThermostat: DeviceThermostatIcon,
  Thermostat: ThermostatIcon,
  Air: AirIcon,
  Compress: CompressIcon,
  LightMode: LightModeIcon,
  WbSunny: WbSunnyIcon,
};

const METER_COMPONENTS = {
  gauge: GaugeMeter,
  thermometer: ThermometerMeter,
  level: LevelMeter,
  humidity: HumidityMeter,
  co2: CO2Meter,
  light: LightMeter,
};

// Get mock value from shared mock data
const getMockValue = (sensorType) => {
  return MOCK_SENSOR_VALUES[sensorType] ?? 50;
};

// Get mock RGB data for PAR sensor from shared mock data
const getMockRGB = () => MOCK_RGB_DATA;

// Get mock trend from shared mock data
const getMockTrend = (sensorType) => {
  return MOCK_TRENDS[sensorType] ?? { value: 0, direction: 'neutral' };
};

// RGB Spectrum Bar Component - shows distribution of light spectrum
function RGBSpectrumBar({ rgb }) {
  const total = rgb.r + rgb.g + rgb.b;
  const rPercent = (rgb.r / total) * 100;
  const gPercent = (rgb.g / total) * 100;
  const bPercent = (rgb.b / total) * 100;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Stacked spectrum bar */}
      <Box sx={{
        width: '100%',
        height: 10,
        borderRadius: 1,
        overflow: 'hidden',
        display: 'flex',
        mb: 0.5,
      }}>
        <Box sx={{ width: `${rPercent}%`, bgcolor: '#ef4444', height: '100%' }} />
        <Box sx={{ width: `${gPercent}%`, bgcolor: '#22c55e', height: '100%' }} />
        <Box sx={{ width: `${bPercent}%`, bgcolor: '#3b82f6', height: '100%' }} />
      </Box>
      {/* RGB Values row */}
      <Stack direction="row" justifyContent="space-between" sx={{ px: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444' }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.55rem', color: '#ef4444', fontWeight: 600 }}>
            {rgb.r.toFixed(1)}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e' }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.55rem', color: '#22c55e', fontWeight: 600 }}>
            {rgb.g.toFixed(1)}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#3b82f6' }} />
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.55rem', color: '#3b82f6', fontWeight: 600 }}>
            {rgb.b.toFixed(1)}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}

// Gradient component for RGB sparklines
function RGBAreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0.05} />
      </linearGradient>
    </defs>
  );
}

// RGB Multi-Sparkline Chart Component with gradient fills
function RGBSparklineChart({ rgb }) {
  // Generate mock historical data for each channel
  const generateChannelData = (currentValue) => {
    const data = [];
    for (let i = 0; i < 30; i++) {
      const variation = (Math.random() - 0.5) * currentValue * 0.15;
      data.push(Math.max(0, currentValue + variation));
    }
    data[data.length - 1] = currentValue;
    return data;
  };

  const rData = React.useMemo(() => generateChannelData(rgb.r), [rgb.r]);
  const gData = React.useMemo(() => generateChannelData(rgb.g), [rgb.g]);
  const bData = React.useMemo(() => generateChannelData(rgb.b), [rgb.b]);

  // Unique gradient IDs
  const rGradientId = React.useMemo(() => `rgb-r-gradient-${Math.random().toString(36).substr(2, 9)}`, []);
  const gGradientId = React.useMemo(() => `rgb-g-gradient-${Math.random().toString(36).substr(2, 9)}`, []);
  const bGradientId = React.useMemo(() => `rgb-b-gradient-${Math.random().toString(36).substr(2, 9)}`, []);

  return (
    <Stack spacing={0.25} sx={{ width: '100%' }}>
      {/* Red channel */}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#ef4444', flexShrink: 0 }} />
        <Box sx={{ flex: 1, height: 16 }}>
          <SparkLineChart
            data={rData}
            height={16}
            colors={['#ef4444']}
            curve="natural"
            area
            sx={{ [`& .${areaElementClasses.root}`]: { fill: `url(#${rGradientId})` } }}
          >
            <RGBAreaGradient color="#ef4444" id={rGradientId} />
          </SparkLineChart>
        </Box>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.5rem', color: '#ef4444', fontWeight: 600, minWidth: 28, textAlign: 'right' }}>
          {rgb.r.toFixed(1)}
        </Typography>
      </Stack>
      {/* Green channel */}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0 }} />
        <Box sx={{ flex: 1, height: 16 }}>
          <SparkLineChart
            data={gData}
            height={16}
            colors={['#22c55e']}
            curve="natural"
            area
            sx={{ [`& .${areaElementClasses.root}`]: { fill: `url(#${gGradientId})` } }}
          >
            <RGBAreaGradient color="#22c55e" id={gGradientId} />
          </SparkLineChart>
        </Box>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.5rem', color: '#22c55e', fontWeight: 600, minWidth: 28, textAlign: 'right' }}>
          {rgb.g.toFixed(1)}
        </Typography>
      </Stack>
      {/* Blue channel */}
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#3b82f6', flexShrink: 0 }} />
        <Box sx={{ flex: 1, height: 16 }}>
          <SparkLineChart
            data={bData}
            height={16}
            colors={['#3b82f6']}
            curve="natural"
            area
            sx={{ [`& .${areaElementClasses.root}`]: { fill: `url(#${bGradientId})` } }}
          >
            <RGBAreaGradient color="#3b82f6" id={bGradientId} />
          </SparkLineChart>
        </Box>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.5rem', color: '#3b82f6', fontWeight: 600, minWidth: 28, textAlign: 'right' }}>
          {rgb.b.toFixed(1)}
        </Typography>
      </Stack>
    </Stack>
  );
}

// Custom PAR Meter - Circular style with RGB outer ring and user color inner ring
function PARMeterWithRGB({ value, min, max, optimalMin, optimalMax, rgb, size = 100, color = '#f59e0b', rgbOptimal }) {
  const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);
  const centerX = size / 2;
  const centerY = size / 2;

  // Ring dimensions
  const outerRadius = size * 0.42;
  const innerRadius = size * 0.28;
  const outerStrokeWidth = 8;
  const innerStrokeWidth = 10;

  // Default RGB optimal ranges (can be passed as prop)
  const rgbOpt = rgbOptimal || {
    r: { min: 150, max: 200 },
    g: { min: 100, max: 140 },
    b: { min: 35, max: 55 },
  };

  // Calculate RGB percentages for the ring segments
  const total = rgb.r + rgb.g + rgb.b;
  const rPercent = rgb.r / total;
  const gPercent = rgb.g / total;
  const bPercent = rgb.b / total;

  // Outer ring circumference
  const outerCircum = 2 * Math.PI * outerRadius;
  const filledLength = outerCircum * normalizedValue;

  // Inner ring circumference
  const innerCircum = 2 * Math.PI * innerRadius;
  const innerFilled = innerCircum * normalizedValue;

  // Calculate arc segments for RGB on outer ring
  const rLength = filledLength * rPercent;
  const gLength = filledLength * gPercent;
  const bLength = filledLength * bPercent;

  // Optimal range arc positions (for inner green arc)
  const optMinNorm = optimalMin !== undefined ? (optimalMin - min) / (max - min) : 0.4;
  const optMaxNorm = optimalMax !== undefined ? (optimalMax - min) / (max - min) : 0.67;
  const optimalStartAngle = -90 + (optMinNorm * 360);
  const optimalArcLength = innerCircum * (optMaxNorm - optMinNorm);

  // RGB optimal range bar positions (outer edge)
  const rgbOptRadius = outerRadius + outerStrokeWidth / 2 + 3;
  const calcOptimalArc = (currentVal, optMin, optMax, maxVal) => {
    const minNorm = optMin / maxVal;
    const maxNorm = optMax / maxVal;
    return {
      startAngle: -90 + (minNorm * 360),
      length: 2 * Math.PI * rgbOptRadius * (maxNorm - minNorm),
    };
  };

  // Max values for RGB normalization (assuming ~200 for R, ~150 for G, ~80 for B as typical max)
  const rOptArc = calcOptimalArc(rgb.r, rgbOpt.r.min, rgbOpt.r.max, 250);
  const gOptArc = calcOptimalArc(rgb.g, rgbOpt.g.min, rgbOpt.g.max, 180);
  const bOptArc = calcOptimalArc(rgb.b, rgbOpt.b.min, rgbOpt.b.max, 100);

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      width: '100%',
      maxWidth: size,
      margin: '0 auto'
    }}>
      <svg 
        width="100%" 
        height="auto" 
        viewBox={`0 0 ${size} ${size}`}
        style={{ maxWidth: `${size}px` }}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="parGlowConfig">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Outer RGB optimal range bars (on outer edge) */}
        <circle
          cx={centerX}
          cy={centerY}
          r={rgbOptRadius}
          fill="none"
          stroke="#ef4444"
          strokeWidth={2}
          strokeDasharray={`${rOptArc.length} ${2 * Math.PI * rgbOptRadius - rOptArc.length}`}
          strokeLinecap="round"
          opacity={0.6}
          transform={`rotate(${rOptArc.startAngle} ${centerX} ${centerY})`}
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={rgbOptRadius}
          fill="none"
          stroke="#22c55e"
          strokeWidth={2}
          strokeDasharray={`${gOptArc.length} ${2 * Math.PI * rgbOptRadius - gOptArc.length}`}
          strokeLinecap="round"
          opacity={0.6}
          transform={`rotate(${gOptArc.startAngle} ${centerX} ${centerY})`}
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={rgbOptRadius}
          fill="none"
          stroke="#3b82f6"
          strokeWidth={2}
          strokeDasharray={`${bOptArc.length} ${2 * Math.PI * rgbOptRadius - bOptArc.length}`}
          strokeLinecap="round"
          opacity={0.6}
          transform={`rotate(${bOptArc.startAngle} ${centerX} ${centerY})`}
        />

        {/* Outer RGB ring background */}
        <circle
          cx={centerX}
          cy={centerY}
          r={outerRadius}
          fill="none"
          stroke="#1f2937"
          strokeWidth={outerStrokeWidth}
        />

        {/* Outer RGB segments (Blue, Green, Red stacked) */}
        <circle
          cx={centerX}
          cy={centerY}
          r={outerRadius}
          fill="none"
          stroke="#3b82f6"
          strokeWidth={outerStrokeWidth}
          strokeDasharray={`${bLength} ${outerCircum - bLength}`}
          strokeDashoffset={outerCircum * 0.25}
          strokeLinecap="round"
          filter="url(#parGlowConfig)"
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={outerRadius}
          fill="none"
          stroke="#22c55e"
          strokeWidth={outerStrokeWidth}
          strokeDasharray={`${gLength} ${outerCircum - gLength}`}
          strokeDashoffset={outerCircum * 0.25 - bLength}
          strokeLinecap="round"
          filter="url(#parGlowConfig)"
        />
        <circle
          cx={centerX}
          cy={centerY}
          r={outerRadius}
          fill="none"
          stroke="#ef4444"
          strokeWidth={outerStrokeWidth}
          strokeDasharray={`${rLength} ${outerCircum - rLength}`}
          strokeDashoffset={outerCircum * 0.25 - bLength - gLength}
          strokeLinecap="round"
          filter="url(#parGlowConfig)"
        />

        {/* Inner ring background */}
        <circle
          cx={centerX}
          cy={centerY}
          r={innerRadius}
          fill="none"
          stroke="#1f2937"
          strokeWidth={innerStrokeWidth}
        />

        {/* Inner optimal range arc (green) */}
        <circle
          cx={centerX}
          cy={centerY}
          r={innerRadius - innerStrokeWidth / 2 - 2}
          fill="none"
          stroke="#22c55e"
          strokeWidth={3}
          strokeDasharray={`${optimalArcLength} ${innerCircum - optimalArcLength}`}
          strokeLinecap="round"
          opacity={0.4}
          transform={`rotate(${optimalStartAngle} ${centerX} ${centerY})`}
        />

        {/* Inner user color ring (sensor value) */}
        <circle
          cx={centerX}
          cy={centerY}
          r={innerRadius}
          fill="none"
          stroke={color}
          strokeWidth={innerStrokeWidth}
          strokeDasharray={`${innerFilled} ${innerCircum - innerFilled}`}
          strokeDashoffset={innerCircum * 0.25}
          strokeLinecap="round"
          filter="url(#parGlowConfig)"
        />

        {/* Center text - PAR value */}
        <text
          x={centerX}
          y={centerY - 2}
          textAnchor="middle"
          dominantBaseline="middle"
          fill={color}
          fontSize={12}
          fontWeight={700}
          fontFamily="Inter, sans-serif"
        >
          {value.toFixed(0)}
        </text>
        <text
          x={centerX}
          y={centerY + 10}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#9ca3af"
          fontSize={5}
          fontFamily="Inter, sans-serif"
        >
          µmol/m²/s
        </text>
      </svg>
    </Box>
  );
}

// Custom Ring Meter for DLI sensor - Circular style similar to humidity with optimal range
function DLIRingMeter({ value, min, max, optimalMin, optimalMax, color = '#f59e0b', size = 100 }) {
  const normalizedValue = Math.min(Math.max((value - min) / (max - min), 0), 1);
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = size * 0.38;
  const strokeWidth = 12;
  const circumference = 2 * Math.PI * radius;
  const filledLength = circumference * normalizedValue;

  // Calculate optimal range arc
  const optMinNorm = optimalMin !== undefined ? (optimalMin - min) / (max - min) : 0.46;
  const optMaxNorm = optimalMax !== undefined ? (optimalMax - min) / (max - min) : 0.77;
  const optimalStartAngle = -90 + (optMinNorm * 360);
  const optimalArcLength = circumference * (optMaxNorm - optMinNorm);



  const progressColor = color;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <defs>
          <filter id="dliRingGlowConfig">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Background ring */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke="#1f2937"
          strokeWidth={strokeWidth}
        />

        {/* Optimal range arc (outer, green indicator) */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius + strokeWidth / 2 + 4}
          fill="none"
          stroke="#22c55e"
          strokeWidth={3}
          strokeDasharray={`${optimalArcLength} ${circumference - optimalArcLength}`}
          strokeLinecap="round"
          opacity={0.4}
          transform={`rotate(${optimalStartAngle} ${centerX} ${centerY})`}
        />

        {/* Progress arc */}
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill="none"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeDasharray={`${filledLength} ${circumference - filledLength}`}
          strokeDashoffset={circumference * 0.25}
          strokeLinecap="round"
          filter="url(#dliRingGlowConfig)"
        />

        {/* Sun icon in center */}
        <g transform={`translate(${centerX - 12}, ${centerY - 22})`}>
          <circle cx={12} cy={12} r={6} fill={progressColor} opacity={0.8} />
          {/* Sun rays */}
          {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
            <line
              key={i}
              x1={12}
              y1={12}
              x2={12 + Math.cos(angle * Math.PI / 180) * 10}
              y2={12 + Math.sin(angle * Math.PI / 180) * 10}
              stroke={progressColor}
              strokeWidth={1.5}
              strokeLinecap="round"
              opacity={0.6}
            />
          ))}
        </g>

        {/* Value text */}
        <text
          x={centerX}
          y={centerY + radius * 0.55}
          textAnchor="middle"
          fill="#e5e7eb"
          fontSize={11}
          fontWeight={600}
          fontFamily="Inter, sans-serif"
        >
          {value.toFixed(1)}
        </text>
      </svg>
    </Box>
  );
}

function ConfigurableSensorCard({ sectionId, sensor, growId, isEditMode }) {
  const { showAllAsPlots } = useSensorView();
  const { getOptimalRange, grows, updateGrowDashboardConfig } = useGrow();
  const location = useLocation();
  const [localOverride, setLocalOverride] = React.useState(null);
  const [showSettings, setShowSettings] = React.useState(false);

  // Detect demo mode
  const isDemo = location.pathname.startsWith('/demo');

  // Always call the hook unconditionally (returns null if not in provider)
  const liveSensorContext = useLiveSensors();
  // Only use live data in demo mode
  const liveSensorData = isDemo ? liveSensorContext : null;

  const showPlot = localOverride !== null ? localOverride : showAllAsPlots;

  // Get sensor color (falls back to default color if not set)
  const sensorColor = sensor.color || '#22c55e';

  // Get sensor type configuration with preset optimal values if available
  const presetOptimal = getOptimalRange(sensor.type);
  const typeConfig = getSensorConfigWithPreset(sensor.type, presetOptimal) || SENSOR_TYPES[sensor.type] || {};

  // Get sensor name (use sensor.name if set, otherwise use type label)
  const sensorName = sensor.name || typeConfig.label || sensor.type;

  // Get section sensors for move operations
  const getSectionSensors = () => {
    if (!growId) return [];
    const currentGrow = grows.find(g => g.id === growId);
    const sections = currentGrow?.dashboardConfig?.sections || [];
    const section = sections.find(s => s.id === sectionId);
    return section?.sensors || [];
  };

  const sectionSensors = getSectionSensors();
  const sensorIndex = sectionSensors.findIndex(s => s.id === sensor.id);
  const isFirst = sensorIndex === 0;
  const isLast = sensorIndex === sectionSensors.length - 1;

  // Move sensor left/right
  const moveSensor = (direction) => {
    if (!growId) return;
    const currentGrow = grows.find(g => g.id === growId);
    const sections = currentGrow?.dashboardConfig?.sections || [];
    const updatedSections = sections.map(s => {
      if (s.id === sectionId) {
        const sensors = [...s.sensors];
        const idx = sensors.findIndex(sen => sen.id === sensor.id);
        if (idx === -1) return s;
        const newIdx = direction === 'left' ? idx - 1 : idx + 1;
        if (newIdx < 0 || newIdx >= sensors.length) return s;
        [sensors[idx], sensors[newIdx]] = [sensors[newIdx], sensors[idx]];
        return { ...s, sensors };
      }
      return s;
    });
    updateGrowDashboardConfig(growId, { sections: updatedSections });
  };

  // Delete sensor from section
  const deleteSensor = () => {
    if (!growId) return;
    const currentGrow = grows.find(g => g.id === growId);
    const sections = currentGrow?.dashboardConfig?.sections || [];
    const updatedSections = sections.map(s => {
      if (s.id === sectionId) {
        return {
          ...s,
          sensors: s.sensors.filter(sen => sen.id !== sensor.id),
        };
      }
      return s;
    });
    updateGrowDashboardConfig(growId, { sections: updatedSections });
  };
  const {
    unit = '',
    min = 0,
    max = 100,
    optimalMin = 25,
    optimalMax = 75,
    meterType = 'gauge',
    icon = 'WaterDrop',
  } = typeConfig;

  // Get value and trend from live data in demo mode, otherwise use mock
  const value = React.useMemo(() => {
    if (liveSensorData && liveSensorData.sensorValues[sensor.type] !== undefined) {
      return liveSensorData.sensorValues[sensor.type];
    }
    return getMockValue(sensor.type);
  }, [liveSensorData, sensor.type]);

  const trend = React.useMemo(() => {
    if (liveSensorData && liveSensorData.sensorTrends[sensor.type]) {
      return liveSensorData.sensorTrends[sensor.type];
    }
    return getMockTrend(sensor.type);
  }, [liveSensorData, sensor.type]);

  // RGB data for PAR sensor
  const rgb = React.useMemo(() => {
    if (sensor.type === 'ppfd') {
      // Generate slightly varying RGB based on value for more realistic effect
      if (liveSensorData) {
        const baseRGB = getMockRGB();
        const variation = (Math.sin(Date.now() / 5000) * 0.05);
        return {
          r: baseRGB.r * (1 + variation),
          g: baseRGB.g * (1 + variation * 0.8),
          b: baseRGB.b * (1 - variation * 0.5),
        };
      }
      return getMockRGB();
    }
    return null;
  }, [sensor.type, liveSensorData]);

  const meterSize = sensor.size === 'big' ? 120 : 100;

  // Check if this is a special sensor type
  const isPARSensor = sensor.type === 'ppfd';
  const isDLISensor = sensor.type === 'dli';

  const IconComponent = ICON_MAP[icon] || WaterDropIcon;

  // Get status color using gradient based on distance from optimal
  const getStatusColor = () => {
    const { color } = getStatusColorGradient(value, min, max, optimalMin, optimalMax);
    return color;
  };

  const getStatusText = () => {
    const { status } = getStatusColorGradient(value, min, max, optimalMin, optimalMax);
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const MeterComponent = METER_COMPONENTS[meterType] || GaugeMeter;

  const meterProps = {
    value,
    min,
    max,
    optimalMin,
    optimalMax,
    size: meterSize,
    color: sensorColor,
  };

  // Add special colors for pH/ORP gauges
  if (meterType === 'gauge' && (sensor.type === 'ph' || sensor.type === 'orp')) {
    meterProps.colors = GaugeMeter.COLOR_PRESETS.SYMMETRIC_7;
    meterProps.segments = 7;
  }

  const toggleView = () => {
    setLocalOverride(prev => prev === null ? !showAllAsPlots : !prev);
  };

  // Render the appropriate meter based on sensor type
  const renderMeter = () => {
    if (isPARSensor && rgb) {
      return (
        <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
          <PARMeterWithRGB
            value={value}
            min={min}
            max={max}
            optimalMin={optimalMin}
            optimalMax={optimalMax}
            rgb={rgb}
            size={meterSize}
            color={sensorColor}
          />
          <RGBSpectrumBar rgb={rgb} />
        </Stack>
      );
    }
    if (isDLISensor) {
      return (
        <DLIRingMeter
          value={value}
          min={min}
          max={max}
          optimalMin={optimalMin}
          optimalMax={optimalMax}
          color={sensorColor}
          size={meterSize}
        />
      );
    }
    return <MeterComponent {...meterProps} />;
  };

  // Render plot view based on sensor type
  const renderPlot = () => {
    if (isPARSensor && rgb) {
      return (
        <Stack spacing={0.5} sx={{ width: '100%' }}>
          <Box sx={{ width: '100%', height: 40 }}>
            <TimeSeriesPlot value={value} min={min} max={max} optimalMin={optimalMin} optimalMax={optimalMax} size={40} color={sensorColor} fullWidth={true} />
          </Box>
          <RGBSparklineChart rgb={rgb} />
        </Stack>
      );
    }
    return (
      <TimeSeriesPlot
        value={value}
        min={min}
        max={max}
        optimalMin={optimalMin}
        optimalMax={optimalMax}
        size={meterSize}
        color={sensorColor}
        fullWidth={true}
      />
    );
  };

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ flexGrow: 1, pt: 0.75, pb: 1.5, px: 1.5, display: 'flex', flexDirection: 'column' }}>
        <Stack spacing={0.5} alignItems="center" sx={{ flexGrow: 1 }}>
          {/* Header with icon, toggle, and trend - px: 0.5 matches bottom range info */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', px: 0.5, mb: 0.25 }}>
            {/* Icon - clickable to open settings */}
            <Tooltip title="Sensor Settings">
              <Box
                onClick={() => setShowSettings(true)}
                sx={{
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  borderRadius: 1,
                  bgcolor: sensorColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: 14,
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  },
                }}
              >
                <IconComponent sx={{ fontSize: 16 }} />
              </Box>
            </Tooltip>

            {/* Toggle button */}
            <Tooltip title={showPlot ? 'Show Meter' : 'Show Chart'}>
              <IconButton
                size="small"
                onClick={toggleView}
                sx={{
                  bgcolor: showPlot ? 'primary.main' : 'action.hover',
                  color: showPlot ? 'white' : 'text.secondary',
                  '&:hover': {
                    bgcolor: showPlot ? 'primary.dark' : 'action.selected',
                  },
                  width: 28,
                  height: 28,
                  minWidth: 28,
                  borderRadius: 1,
                }}
              >
                {showPlot ? <SpeedIcon sx={{ fontSize: 16 }} /> : <ShowChartIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>

            {/* Trend indicator - 28x28 box same as other buttons */}
            <Box
              sx={{
                width: 28,
                height: 28,
                minWidth: 28,
                borderRadius: 1,
                bgcolor: trend.direction === 'up'
                  ? 'success.main'
                  : trend.direction === 'down'
                    ? 'error.main'
                    : 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: trend.direction !== 'neutral' ? 'white' : 'text.secondary',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.55rem',
                  lineHeight: 1,
                }}
              >
                {trend.direction === 'up' ? '+' : trend.direction === 'down' ? '-' : ''}
                {Math.abs(trend.value).toFixed(1)}
              </Typography>
            </Box>
          </Stack>

          {/* Title */}
          <Typography
            variant="subtitle2"
            component="h3"
            sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif', fontSize: '0.8rem' }}
          >
            {sensorName}
          </Typography>

          {/* Status text */}
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textAlign: 'center',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.65rem',
            }}
          >
            Status:{' '}
            <Box component="span" sx={{ color: getStatusColor(), fontWeight: 600 }}>
              {getStatusText()}
            </Box>
          </Typography>

          {/* Meter or Plot visualization - centered both horizontally and vertically */}
          <Box sx={{
            width: '100%',
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: meterSize + 20,
            py: 1,
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}>
              {showPlot ? renderPlot() : renderMeter()}
            </Box>
          </Box>

          {/* Bottom section */}
          <Box sx={{ width: '100%' }}>
            {/* Value display */}
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                mb: 0.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 700,
                  color: getStatusColor(),
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.95rem',
                  textAlign: 'center',
                }}
              >
                {typeof value === 'number' ? value.toFixed(1) : value} {unit}
              </Typography>
            </Box>

            {/* Range info - px: 0.5 same as header buttons */}
            <Stack direction="row" justifyContent="space-between" sx={{ width: '100%', px: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.55rem', fontFamily: 'Inter, sans-serif' }}
              >
                Min: {min}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.55rem', fontFamily: 'Inter, sans-serif' }}
              >
                Opt: {optimalMin}-{optimalMax}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontSize: '0.55rem', fontFamily: 'Inter, sans-serif' }}
              >
                Max: {max}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </CardContent>

      {/* Edit mode overlay - move arrows on sides, delete at top right */}
      {isEditMode && (
        <>
          {/* Left arrow - vertically centered */}
          <Box
            sx={{
              position: 'absolute',
              left: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
            }}
          >
            <Tooltip title="Move left">
              <span>
                <IconButton
                  size="small"
                  onClick={() => moveSensor('left')}
                  disabled={isFirst}
                  sx={{
                    bgcolor: 'action.hover',
                    width: 28,
                    height: 28,
                    '&:hover': { bgcolor: 'action.selected' },
                    '&.Mui-disabled': { opacity: 0.3 },
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {/* Right arrow - vertically centered */}
          <Box
            sx={{
              position: 'absolute',
              right: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
            }}
          >
            <Tooltip title="Move right">
              <span>
                <IconButton
                  size="small"
                  onClick={() => moveSensor('right')}
                  disabled={isLast}
                  sx={{
                    bgcolor: 'action.hover',
                    width: 28,
                    height: 28,
                    '&:hover': { bgcolor: 'action.selected' },
                    '&.Mui-disabled': { opacity: 0.3 },
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {/* Delete button - top right */}
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              zIndex: 11,
            }}
          >
            <Tooltip title="Remove sensor">
              <IconButton
                size="small"
                onClick={deleteSensor}
                sx={{
                  bgcolor: 'error.main',
                  color: 'white',
                  width: 24,
                  height: 24,
                  '&:hover': { bgcolor: 'error.dark' },
                }}
              >
                <DeleteIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      {/* Settings Overlay */}
      {showSettings && (
        <SensorSettingsOverlay
          sectionId={sectionId}
          sensor={sensor}
          growId={growId}
          onClose={() => setShowSettings(false)}
        />
      )}
    </Card>
  );
}

ConfigurableSensorCard.propTypes = {
  sectionId: PropTypes.string.isRequired,
  sensor: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    name: PropTypes.string,
    size: PropTypes.string,
    dataPath: PropTypes.string,
    color: PropTypes.string,
  }).isRequired,
  growId: PropTypes.string,
  isEditMode: PropTypes.bool,
};

export default ConfigurableSensorCard;
